// Payment Terms Selector Component
// Multi-select dropdown for choosing payment terms with custom option support

import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Standard payment terms with descriptions
const PAYMENT_TERMS = [
  { code: "100_ADVANCE", label: "100% Advance Payment", description: "Full payment required before production/shipment", category: "advance" },
  { code: "50_50_ADVANCE_SHIPMENT", label: "50% Advance, 50% Before Shipment", description: "Half payment upfront, half before goods are shipped", category: "split" },
  { code: "30_70_ADVANCE_SHIPMENT", label: "30% Advance, 70% Before Shipment", description: "30% payment upfront, 70% before goods are shipped", category: "split" },
  { code: "LETTER_OF_CREDIT", label: "Letter of Credit (LC)", description: "Bank-backed payment guarantee upon document presentation", category: "trade_finance" },
  { code: "DOCUMENTS_AGAINST_PAYMENT", label: "Documents against Payment (D/P)", description: "Documents released when buyer pays through their bank", category: "trade_finance" },
  { code: "DOCUMENTS_AGAINST_ACCEPTANCE", label: "Documents against Acceptance (D/A)", description: "Documents released when buyer accepts bill of exchange", category: "trade_finance" },
  { code: "OPEN_ACCOUNT", label: "Open Account", description: "Payment after goods received (typically 30-90 days)", category: "credit" },
  { code: "ESCROW", label: "Escrow", description: "Third-party holds payment until delivery confirmed", category: "escrow" },
  { code: "CASH_AGAINST_DELIVERY", label: "Cash Against Delivery (CAD)", description: "Payment upon delivery of goods", category: "delivery" },
  { code: "NEGOTIABLE", label: "Negotiable", description: "Payment terms open for discussion based on order", category: "flexible" },
  { code: "CUSTOM", label: "Custom Payment Terms", description: "Seller-defined custom payment arrangement", category: "custom" }
];

export default function PaymentTermsSelector({
  value = [],
  onChange,
  customTerms = '',
  onCustomTermsChange,
  negotiable = false,
  onNegotiableChange,
  showNegotiableOption = true,
  showCustomOption = true,
  showSafetyNotice = true,
  maxSelections = 5,
  disabled = false,
  label = "Payment Terms",
  placeholder = "Select payment terms...",
  role = "seller" // 'seller' or 'buyer'
}) {
  const [open, setOpen] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState(value || []);

  useEffect(() => {
    setSelectedTerms(value || []);
  }, [value]);

  const handleTermToggle = (code) => {
    let newSelection;
    if (selectedTerms.includes(code)) {
      newSelection = selectedTerms.filter(c => c !== code);
    } else {
      if (selectedTerms.length >= maxSelections) {
        return; // Don't allow more than max
      }
      newSelection = [...selectedTerms, code];
    }
    setSelectedTerms(newSelection);
    onChange && onChange(newSelection);
  };

  const handleRemoveTerm = (code) => {
    const newSelection = selectedTerms.filter(c => c !== code);
    setSelectedTerms(newSelection);
    onChange && onChange(newSelection);
  };

  const getTermLabel = (code) => {
    const term = PAYMENT_TERMS.find(t => t.code === code);
    return term ? term.label : code;
  };

  const showCustomInput = selectedTerms.includes('CUSTOM');

  const availableTerms = showCustomOption 
    ? PAYMENT_TERMS 
    : PAYMENT_TERMS.filter(t => t.code !== 'CUSTOM');

  return (
    <div className="space-y-3" data-testid="payment-terms-selector">
      <Label>{label}</Label>
      
      {/* Selected Terms Display */}
      {selectedTerms.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedTerms.map(code => (
            <Badge 
              key={code} 
              variant="secondary"
              className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200"
            >
              {getTermLabel(code)}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveTerm(code)}
                  className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Dropdown Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            disabled={disabled}
            data-testid="payment-terms-trigger"
          >
            {selectedTerms.length === 0 
              ? <span className="text-muted-foreground">{placeholder}</span>
              : `${selectedTerms.length} term(s) selected`
            }
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <div className="max-h-[300px] overflow-y-auto p-2">
            {/* Category: Advance */}
            <div className="mb-3">
              <p className="text-xs font-medium text-slate-500 px-2 mb-1">ADVANCE PAYMENTS</p>
              {availableTerms.filter(t => t.category === 'advance' || t.category === 'split').map(term => (
                <TermOption
                  key={term.code}
                  term={term}
                  selected={selectedTerms.includes(term.code)}
                  onToggle={() => handleTermToggle(term.code)}
                  disabled={disabled || (!selectedTerms.includes(term.code) && selectedTerms.length >= maxSelections)}
                />
              ))}
            </div>

            {/* Category: Trade Finance */}
            <div className="mb-3">
              <p className="text-xs font-medium text-slate-500 px-2 mb-1">TRADE FINANCE</p>
              {availableTerms.filter(t => t.category === 'trade_finance' || t.category === 'escrow').map(term => (
                <TermOption
                  key={term.code}
                  term={term}
                  selected={selectedTerms.includes(term.code)}
                  onToggle={() => handleTermToggle(term.code)}
                  disabled={disabled || (!selectedTerms.includes(term.code) && selectedTerms.length >= maxSelections)}
                />
              ))}
            </div>

            {/* Category: Other */}
            <div>
              <p className="text-xs font-medium text-slate-500 px-2 mb-1">OTHER</p>
              {availableTerms.filter(t => ['credit', 'delivery', 'flexible', 'custom'].includes(t.category)).map(term => (
                <TermOption
                  key={term.code}
                  term={term}
                  selected={selectedTerms.includes(term.code)}
                  onToggle={() => handleTermToggle(term.code)}
                  disabled={disabled || (!selectedTerms.includes(term.code) && selectedTerms.length >= maxSelections)}
                />
              ))}
            </div>
          </div>
          
          {selectedTerms.length >= maxSelections && (
            <div className="p-2 bg-amber-50 border-t text-xs text-amber-700">
              Maximum {maxSelections} terms can be selected
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Custom Terms Input */}
      {showCustomInput && (
        <div className="space-y-2">
          <Label htmlFor="custom-terms" className="text-sm">
            Custom Payment Terms Description
          </Label>
          <Textarea
            id="custom-terms"
            value={customTerms}
            onChange={(e) => onCustomTermsChange && onCustomTermsChange(e.target.value)}
            placeholder="Describe your custom payment terms..."
            className="h-20"
            disabled={disabled}
            data-testid="custom-terms-input"
          />
        </div>
      )}

      {/* Negotiable Option */}
      {showNegotiableOption && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="negotiable"
            checked={negotiable}
            onCheckedChange={(checked) => onNegotiableChange && onNegotiableChange(checked)}
            disabled={disabled}
            data-testid="negotiable-checkbox"
          />
          <label
            htmlFor="negotiable"
            className="text-sm text-slate-600 cursor-pointer"
          >
            {role === 'seller' 
              ? 'Payment terms are negotiable based on order value'
              : 'I am flexible on payment terms'
            }
          </label>
        </div>
      )}

      {/* Safety Notice */}
      {showSafetyNotice && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-xs text-amber-800">
            {role === 'seller' 
              ? 'Payment terms shown are indicative and may be negotiated. GlobalVyapar does not guarantee payment, buyer solvency, delivery, or transaction completion.'
              : 'Payment terms are subject to seller approval. For high-value orders, consider using Letter of Credit or escrow through a licensed provider.'
            }
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// Individual term option component
function TermOption({ term, selected, onToggle, disabled }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer transition-colors",
              selected ? "bg-blue-50" : "hover:bg-slate-50",
              disabled && !selected && "opacity-50 cursor-not-allowed"
            )}
            onClick={() => !disabled && onToggle()}
            data-testid={`term-option-${term.code}`}
          >
            <Checkbox
              checked={selected}
              onCheckedChange={() => !disabled && onToggle()}
              disabled={disabled && !selected}
              className="pointer-events-none"
            />
            <div className="flex-1">
              <p className={cn(
                "text-sm font-medium",
                selected ? "text-blue-700" : "text-slate-700"
              )}>
                {term.label}
              </p>
            </div>
            <Info className="h-4 w-4 text-slate-400" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[250px]">
          <p className="text-sm">{term.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Display-only component for showing payment terms
export function PaymentTermsDisplay({ terms = [], customTerms, negotiable, compact = false }) {
  if (terms.length === 0 && !customTerms) {
    return (
      <span className="text-sm text-muted-foreground">No payment terms specified</span>
    );
  }

  const getTermLabel = (code) => {
    const term = PAYMENT_TERMS.find(t => t.code === code);
    return term ? term.label : code;
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        {terms.slice(0, 3).map(code => (
          <Badge key={code} variant="outline" className="text-xs">
            {getTermLabel(code)}
          </Badge>
        ))}
        {terms.length > 3 && (
          <Badge variant="outline" className="text-xs">+{terms.length - 3}</Badge>
        )}
        {negotiable && (
          <Badge variant="secondary" className="text-xs bg-green-50 text-green-700">
            Negotiable
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {terms.map(code => (
          <Badge key={code} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
            {getTermLabel(code)}
          </Badge>
        ))}
      </div>
      {customTerms && (
        <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
          <span className="font-medium">Custom: </span>{customTerms}
        </p>
      )}
      {negotiable && (
        <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
          <Check className="h-3 w-3 mr-1" />
          Open to negotiation
        </Badge>
      )}
    </div>
  );
}
